

import type {
  TwiggyConfig,
  TrackingEvent,
  TrackingEventType,
  EventProperties,
  IdentifyData,
  ConsentCategory,
  ProductViewData,
  AddToCartData,
  RemoveFromCartData,
  CheckoutStepData,
  SearchData,
  IdentifyResponse,
  ConsentResponse
} from '../types';

import { resolveConfig, buildApiUrl, type ResolvedConfig } from './config';
import { EventQueue } from './queue';
import { SessionManager } from '../identity/session';
import { VisitorManager } from '../identity/visitor';
import { ConsentManager } from '../identity/consent';
import { AutoCapture } from '../tracking/auto-capture';
import {
  setupVtexIntegration,
  setupGtagIntegration,
  ensureVtexCartAbandonmentTracking,
  setupDataLayerLeadCapture,
  setupDataLayerWishlistCapture
} from '../tracking/ecommerce';

export class Twiggy {
  private config: ResolvedConfig;
  private queue: EventQueue;
  private sessionManager: SessionManager;
  private visitorManager: VisitorManager;
  private consentManager: ConsentManager;
  private autoCapture: AutoCapture | null = null;
  private isInitialized = false;

  constructor(config: TwiggyConfig) {
    this.config = resolveConfig(config);

    
    this.sessionManager = new SessionManager();
    this.visitorManager = new VisitorManager();
    this.consentManager = new ConsentManager(this.config.consent.defaultCategories);

    
    this.queue = new EventQueue({
      config: this.config,
      onFlush: (events, response) => {
        this.log(`Sent ${events.length} events`, response);
      },
      onError: (error, events) => {
        this.log(`Failed to send ${events.length} events`, error);
      }
    });

    
    if (typeof window !== 'undefined') {
      try {
        this.init();
      } catch (err) {
        console.error('[Twiggy] SDK initialization failed:', err);
      }
    }
  }

  
  init(): void {
    if (this.isInitialized) return;

    this.log('Initializing SDK', this.config);

    
    if (this.config.autoCapture.length > 0) {
      this.autoCapture = new AutoCapture({
        features: this.config.autoCapture,
        onEvent: (type, properties) => this.trackInternal(type, properties)
      });
      this.autoCapture.start();
    }

    
    if (this.config.platform === 'tray' && !this.config.vtex) {
      setupGtagIntegration((type, properties) => this.trackInternal(type, properties));
      this.log('Tray integration: gtag / GA4 events enabled');
    }

    if (this.config.vtex) {
      setupVtexIntegration((type, properties) => this.trackInternal(type, properties));
      setupGtagIntegration((type, properties) => this.trackInternal(type, properties));
      ensureVtexCartAbandonmentTracking();
      this.log('VTEX integration enabled (dataLayer + gtag + cart abandonment)');
    }

    // Universal: capture checkout_lead from dataLayer in any platform without touching
    // other ecommerce events (safe wrapper; no-op if dataLayer not present).
    setupDataLayerLeadCapture((type, properties) => this.trackInternal(type, properties));

    if (this.config.wishlistDataLayer) {
      setupDataLayerWishlistCapture(
        (type, properties) => this.trackInternal(type, properties),
        () => this.visitorManager.getVisitorId()
      );
      this.log('wishlistDataLayer: listening for twiggy_wishlist / wishlist_signal');
    }

    this.isInitialized = true;
    this.log('SDK initialized');
  }

  
  track(eventName: string, properties?: EventProperties): void {
    
    const typeMap: Record<string, TrackingEventType> = {
      pageView: 'sdk_page_view',
      page_view: 'sdk_page_view',
      productView: 'sdk_product_view',
      product_view: 'sdk_product_view',
      addToCart: 'sdk_add_to_cart',
      add_to_cart: 'sdk_add_to_cart',
      removeFromCart: 'sdk_remove_from_cart',
      remove_from_cart: 'sdk_remove_from_cart',
      checkoutStep: 'sdk_checkout_step',
      checkout_step: 'sdk_checkout_step',
      search: 'sdk_search',
      cartAbandoned: 'sdk_cart_abandoned',
      cart_abandoned: 'sdk_cart_abandoned',
      checkoutLead: 'sdk_checkout_lead',
      checkout_lead: 'sdk_checkout_lead',
      wishlistSignal: 'sdk_wishlist_signal',
      wishlist_signal: 'sdk_wishlist_signal'
    };

    const type = typeMap[eventName] || (('sdk_' + eventName) as TrackingEventType);
    this.trackInternal(type, properties);
  }

  
  private trackInternal(type: TrackingEventType, properties?: EventProperties): void {
    
    if (!this.hasConsentFor(type)) {
      this.log(`Skipping ${type} - no consent`);
      return;
    }

    const event: TrackingEvent = {
      visitorId: this.visitorManager.getVisitorId(),
      sessionId: this.sessionManager.getSessionId(),
      type,
      properties: {
        ...this.getDefaultProperties(),
        ...properties
      },
      context: this.getContext(),
      consent: this.consentManager.getCategories(),
      occurredAt: new Date().toISOString()
    };

    this.queue.push(event);

    // Carrinho abandonado costuma ser enfileirado no término da página (VTEX: getOrderForm async
    // após pagehide). O listener de pagehide do EventQueue roda antes e já chamou flushSync com a
    // fila vazia — sem este flush, o evento nunca sai (timer de batch não dispara a tempo).
    if (type === 'sdk_cart_abandoned' || type === 'sdk_wishlist_signal') {
      this.queue.flushSync();
    }
  }

  
  pageView(properties?: EventProperties): void {
    this.trackInternal('sdk_page_view', {
      url: window.location.href,
      path: window.location.pathname,
      title: document.title,
      referrer: document.referrer,
      ...properties
    });
  }

  
  productView(data: ProductViewData): void {
    this.trackInternal('sdk_product_view', data);
  }

  
  addToCart(data: AddToCartData): void {
    this.trackInternal('sdk_add_to_cart', {
      ...data,
      quantity: data.quantity || 1
    });
  }

  
  removeFromCart(data: RemoveFromCartData): void {
    this.trackInternal('sdk_remove_from_cart', {
      ...data,
      quantity: data.quantity || 1
    });
  }

  
  checkoutStep(data: CheckoutStepData): void {
    this.trackInternal('sdk_checkout_step', {
      checkoutStep: data.step,
      checkoutStepName: data.stepName,
      cartId: data.cartId,
      cartTotal: data.cartTotal,
      items: data.items
    });
  }

  
  search(data: SearchData): void {
    this.trackInternal('sdk_search', {
      searchQuery: data.query,
      searchResultsCount: data.resultsCount
    });
  }

  
  async identify(data: IdentifyData): Promise<IdentifyResponse> {
    const url = buildApiUrl(this.config, '/identify');

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': this.config.apiKey
      },
      body: JSON.stringify({
        visitorId: this.visitorManager.getVisitorId(),
        sessionId: this.sessionManager.getSessionId(),
        ...data
      })
    });

    if (!response.ok) {
      throw new Error(`Identify failed: ${response.status}`);
    }

    const result: IdentifyResponse = await response.json();
    this.log('Identify result', result);

    return result;
  }

  
  async grantConsent(categories: ConsentCategory[]): Promise<ConsentResponse> {
    this.consentManager.grant(categories);
    return this.updateConsentOnServer('grant', categories);
  }

  
  async revokeConsent(categories: ConsentCategory[]): Promise<ConsentResponse> {
    this.consentManager.revoke(categories);
    return this.updateConsentOnServer('revoke', categories);
  }

  
  private async updateConsentOnServer(
    action: 'grant' | 'revoke',
    categories: ConsentCategory[]
  ): Promise<ConsentResponse> {
    const url = buildApiUrl(this.config, '/consent');

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': this.config.apiKey
      },
      body: JSON.stringify({
        visitorId: this.visitorManager.getVisitorId(),
        sessionId: this.sessionManager.getSessionId(),
        categories,
        action
      })
    });

    if (!response.ok) {
      throw new Error(`Consent update failed: ${response.status}`);
    }

    return response.json();
  }

  
  private hasConsentFor(type: TrackingEventType): boolean {
    
    if (type === 'sdk_consent_granted' || type === 'sdk_consent_revoked') {
      return true;
    }

    
    if (!this.config.consent.required) {
      return true;
    }

    
    return this.consentManager.hasCategory('analytics');
  }

  
  private getDefaultProperties(): EventProperties {
    if (typeof window === 'undefined') return {};

    return {
      url: window.location.href,
      path: window.location.pathname,
      title: document.title,
      referrer: document.referrer
    };
  }

  
  private getContext() {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return {};
    }

    const urlParams = new URLSearchParams(window.location.search);

    return {
      userAgent: navigator.userAgent,
      screenWidth: window.screen?.width,
      screenHeight: window.screen?.height,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      utmSource: urlParams.get('utm_source') || undefined,
      utmMedium: urlParams.get('utm_medium') || undefined,
      utmCampaign: urlParams.get('utm_campaign') || undefined,
      utmTerm: urlParams.get('utm_term') || undefined,
      utmContent: urlParams.get('utm_content') || undefined
    };
  }

  
  getVisitorId(): string {
    return this.visitorManager.getVisitorId();
  }

  
  getSessionId(): string {
    return this.sessionManager.getSessionId();
  }

  
  flush(): Promise<void> {
    return this.queue.flush();
  }

  /**
   * VTEX: chame do GTM quando o checkout estiver pronto, se o pixel subiu antes de `vtexjs.checkout`.
   */
  ensureVtexCartAbandonment(): void {
    if (!this.config.vtex) return;
    ensureVtexCartAbandonmentTracking();
  }

  
  reset(): void {
    this.visitorManager.reset();
    this.sessionManager.reset();
    this.queue.clear();
    this.log('Reset visitor and session');
  }

  
  destroy(): void {
    this.autoCapture?.stop();
    this.queue.flush();
    this.isInitialized = false;
  }

  
  private log(message: string, data?: unknown): void {
    if (this.config.debug) {
      console.log(`[Twiggy] ${message}`, data || '');
    }
  }
}
